import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { useBrand } from "../context/BrandContext";
import { DEFAULT_BRAND } from "../constants/brandDefaults";
import { fetchClubBrandPreview } from "../utils/fetchClubBrandPreview";
import { getDemoJoinPublicUrl } from "../utils/demoClub";
import {
  getDemoSourceClub,
  setDemoSourceClub,
} from "./funtionsBD/Mainservice";
import { isSuperAdminUser } from "../utils/brandAuth";
import ClubLogoMark from "./ClubLogoMark";
import "./DemoClubLookCard.css";

const NONE_CLUB_ID = "";

function BrandSwatches({ brand }) {
  const colors = [
    { key: "primary", value: brand?.color_primary },
    { key: "secondary", value: brand?.color_secondary },
    { key: "accent", value: brand?.color_accent },
  ].filter((c) => c.value);

  if (!colors.length) return null;

  return (
    <div className="demo-look__swatches" aria-hidden>
      {colors.map((c) => (
        <span
          key={c.key}
          className="demo-look__swatch"
          style={{ backgroundColor: c.value }}
          title={c.key}
        />
      ))}
    </div>
  );
}

export default function DemoClubLookCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brand, applyBrandPreview, clearBrandPreview } = useBrand();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const isSuperAdmin = isSuperAdminUser(user);

  const [state, setState] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [hidden, setHidden] = useState(!isSuperAdmin);
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadPreviewForClub = useCallback(
    async (clubId, clubMeta) => {
      setPreviewLoading(true);
      try {
        const resolved = await fetchClubBrandPreview(clubId || null, clubMeta || null);
        applyBrandPreview(resolved);
      } finally {
        setPreviewLoading(false);
      }
    },
    [applyBrandPreview]
  );

  const loadState = useCallback(async () => {
    if (!isSuperAdmin) {
      setHidden(true);
      setLoading(false);
      return;
    }
    try {
      const data = await getDemoSourceClub();
      setState(data);
      const id = data?.source_club?.club_id || "";
      setSelectedId(id);
      setError("");
    } catch (err) {
      const status = err?.statusCode || err?.status || err?.code;
      if (status === 403 || status === 401 || err?.code === 403) {
        setHidden(true);
      } else {
        const raw = typeof err?.message === "string" ? err.message : "";
        setError(
          raw && !/<\s*html/i.test(raw)
            ? raw
            : t("demoLookLoadFailed", "Could not load demo look")
        );
      }
    } finally {
      setLoading(false);
    }
  }, [t, isSuperAdmin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await clearBrandPreview();
      } catch {
        /* keep platform look */
      }
      if (!cancelled) await loadState();
    })();
    return () => {
      cancelled = true;
    };
  }, [clearBrandPreview, loadState]);

  useEffect(() => {
    const handleSourceClubChanged = () => {
      loadState();
    };
    window.addEventListener("demo-source-club-changed", handleSourceClubChanged);
    return () => {
      window.removeEventListener("demo-source-club-changed", handleSourceClubChanged);
    };
  }, [loadState]);

  const clubs = useMemo(() => state?.clubs || [], [state]);

  const noneOption = useMemo(
    () => ({
      club_id: NONE_CLUB_ID,
      club_name: t("demoLookNone", "Demo club only (no simulation)"),
      country: null,
    }),
    [t]
  );

  const clubOptions = useMemo(() => [noneOption, ...clubs], [noneOption, clubs]);

  const selectedClub = useMemo(() => {
    if (!selectedId) return noneOption;
    return clubs.find((c) => c.club_id === selectedId) || state?.source_club || noneOption;
  }, [clubs, selectedId, state, noneOption]);

  const dirty = (selectedId || "") !== (state?.source_club?.club_id || "");

  const filterClubOptions = useCallback((options, { inputValue }) => {
    const q = String(inputValue || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      if (!opt.club_id) return true;
      const name = String(opt.club_name || "").toLowerCase();
      const country = String(opt.country || "").toLowerCase();
      const code = String(opt.club_code || "").toLowerCase();
      return name.includes(q) || country.includes(q) || code.includes(q);
    });
  }, []);

  const handleClubChange = useCallback(
    async (option) => {
      const nextId = option?.club_id || "";
      setSelectedId(nextId);
      await loadPreviewForClub(nextId, option?.club_id ? option : null);
    },
    [loadPreviewForClub]
  );

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const data = await setDemoSourceClub(selectedId || null);
      setState(data);
      const id = data?.source_club?.club_id || "";
      setSelectedId(id);
      await loadPreviewForClub(id, data?.source_club || null);
    } catch (err) {
      setError(err?.message || t("demoLookSaveFailed", "Could not save demo look"));
    } finally {
      setSaving(false);
    }
  };

  const previewLabel = brand?.club_id
    ? brand?.brand_name || selectedClub?.club_name
    : t("demoLookPreviewDefault", "Default SmartTT colours");

  if (hidden || !isSuperAdmin) return null;

  return (
    <section className="demo-look" aria-labelledby="demo-look-title">
      <div className="demo-look__copy">
        <p className="demo-look__kicker">
          {t("demoLookKicker", "Demo session")}
        </p>
        <Typography id="demo-look-title" component="h2" className="demo-look__title">
          {t("demoLookTitle", "Club look for the demo")}
        </Typography>
        <p className="demo-look__lead">
          {t(
            "demoLookLead",
            "Pick a club. The demo uses that club’s colours, logo, and player list. Walk-in nicknames from the phone link still work on top."
          )}
        </p>
      </div>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 56 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {t("loading", "Loading…")}
          </Typography>
        </Box>
      ) : (
        <>
          <div className="demo-look__row">
            {selectedClub?.club_id ? (
              <ClubLogoMark club={selectedClub} size="md" useBrandFallback />
            ) : (
              <div className="demo-look__logo-slot" aria-hidden />
            )}
            <Autocomplete
              className="demo-look__select"
              options={clubOptions}
              value={selectedClub}
              onChange={(_, option) => {
                handleClubChange(option);
              }}
              getOptionLabel={(option) =>
                option?.club_id
                  ? `${option.club_name || ""}${option.country ? ` · ${option.country}` : ""}`
                  : option?.club_name || ""
              }
              isOptionEqualToValue={(a, b) =>
                String(a?.club_id || "") === String(b?.club_id || "")
              }
              filterOptions={filterClubOptions}
              disableClearable
              autoHighlight
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t("demoLookSelect", "Club branding and players")}
                  placeholder={t("demoLookSearchPh", "Search by club name…")}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", mr: 0.75, ml: 0.25 }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.club_id || "none"}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                      py: 0.25,
                    }}
                  >
                    {option.club_id ? (
                      <ClubLogoMark club={option} size="sm" />
                    ) : null}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {option.club_name}
                      </Typography>
                      {option.country ? (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {option.country}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              )}
            />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={handleSave}
              disabled={saving || !dirty || previewLoading}
              sx={{
                minHeight: 42,
                px: 2.4,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {saving ? t("saving", "Saving…") : t("demoLookSave", "Use this club")}
            </Button>
          </div>

          <div className="demo-look__preview" aria-live="polite">
            {previewLoading ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <BrandSwatches brand={brand} />
            )}
            <Typography variant="body2" className="demo-look__preview-label">
              {t("demoLookPreviewing", "Previewing")}: <strong>{previewLabel}</strong>
            </Typography>

            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<QrCode2Icon />}
                onClick={() => setQrOpen(true)}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderColor: "var(--demo-look-line)",
                }}
              >
                {t("demoPhoneLinkTitle", "Phone link & QR")}
              </Button>

              <Button
                variant="contained"
                size="small"
                color="secondary"
                startIcon={<PlayArrowIcon />}
                onClick={() => navigate("/app/training-session")}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  boxShadow: "none",
                }}
              >
                {t("demoLaunchLive", "Launch Live Demo")}
              </Button>
            </Box>
          </div>
        </>
      )}

      {state?.source_club && !loading ? (
        <p className="demo-look__hint">
          {t("demoLookPlayerHint", {
            club: state.source_club.club_name,
            count: state.player_count || 0,
            defaultValue:
              "{{club}} — {{count}} players appear in the demo hall as a simulation. They are not moved to the demo club.",
          })}
        </p>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {/* QR Code Dialog for Walk-in phone joiners */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {t("demoPhoneLinkTitle", "Player Phone Join")}
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "demoPhoneLinkHint",
              "Anyone with this link can enter a nickname on their phone, agree to recording, and join live training to record video."
            )}
          </Typography>
          <Box
            sx={{
              p: 2,
              display: "inline-block",
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              border: "1px solid #e2e8f0",
              mb: 2,
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL || ""}/JoinDemo.png`}
              alt="Demo Phone Join QR Code"
              style={{ width: 200, height: 200, objectFit: "contain", display: "block" }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              background: "#f8fafc",
              py: 1,
              px: 1.5,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography variant="caption" sx={{ wordBreak: "break-all", fontWeight: 600 }}>
              {getDemoJoinPublicUrl()}
            </Typography>
            <Tooltip title={copied ? t("copied", "Copied!") : t("copyLink", "Copy link")}>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(getDemoJoinPublicUrl());
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setQrOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            {t("close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}
